import { Observation } from "@medplum/fhirtypes";

import {
  ClientPostpartumFlowsheetForms,
  NewbornPostpartumFlowsheetForms,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import { getEstimatedBloodLossRowValues } from "@/encounters/components/EncounterLabor/utils/labor";
import {
  getIPPVitalsRowValues,
  getNewbornVitalsRowValues,
} from "@/encounters/components/EncounterLabor/utils/postpartum";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { QuiltedHealthCodes } from "@/fhir/enums/quilted-health";
import { getCodeableConceptCode } from "@/utils/medplum";

/**
 * Result of extracting vitals row values, separating displayable values from metadata.
 */
export interface ExtractedRowValues {
  /** Displayable values for table columns */
  values: Record<string, string | undefined>;
  /** Whether the observation has answers (for EBL timestamp display) */
  hasAnswers: boolean;
}

/**
 * Forms that should be excluded from specific flowsheets in the export.
 * Resuscitation is a Labor form that should only appear in L&D flowsheet, not Client Postpartum.
 */
export const EXCLUDED_CLIENT_POSTPARTUM_FORMS = new Set([
  ClientPostpartumFlowsheetForms.ClientResuscitation,
]);

/**
 * Determine if an observation should be excluded from the flowsheet export.
 */
export function shouldExcludeObservation(observation: Observation, isClient: boolean): boolean {
  if (!isClient) return false;

  const code = getCodeableConceptCode(observation.code);
  return EXCLUDED_CLIENT_POSTPARTUM_FORMS.has(code as ClientPostpartumFlowsheetForms);
}

/**
 * Determine if an observation is a "vitals" type that should show values in table columns.
 * - Client: IPP Vitals (all columns) or Estimated Blood Loss (Est. Bleeding column only)
 * - Newborn: Newborn Vitals (all columns)
 */
export function isVitalsObservation(observation: Observation, isClient: boolean): boolean {
  const code = getCodeableConceptCode(observation.code);

  if (isClient) {
    return (
      code === ClientPostpartumFlowsheetForms.ClientIppVitals ||
      code === ClientPostpartumFlowsheetForms.ClientEstimatedBloodLoss
    );
  } else {
    return code === NewbornPostpartumFlowsheetForms.NewbornVitals;
  }
}

/**
 * Extract vitals row values from a single observation for postpartum flowsheet table.
 * This handles both IPP Vitals (all columns) and Estimated Blood Loss (only Est. Bleeding column).
 * Returns displayable values and metadata separately to avoid coupling.
 */
export function extractSingleObservationRowValues(
  observation: Observation,
  isClient: boolean,
): ExtractedRowValues {
  const values: Record<string, string | undefined> = {};
  const code = getCodeableConceptCode(observation.code);
  let hasAnswers = false;

  if (isClient) {
    if (code === ClientPostpartumFlowsheetForms.ClientIppVitals) {
      // IPP Vitals - all columns
      const vitals = getIPPVitalsRowValues(observation);
      values.bp = vitals.bloodPressure;
      values.temp = vitals.temperature;
      values.pulse = vitals.pulse;
      values.fundus = vitals.fundus
        ? `${vitals.fundus}${vitals.fundusValue ? `: ${vitals.fundusValue}` : ""}${vitals.fundalTexture ? ` (${vitals.fundalTexture})` : ""}`
        : vitals.fundalTexture;
      values.estimatedBleeding = vitals.estimatedBloodLoss || vitals.quantitativeBloodLoss;
      hasAnswers = Object.values(values).some(Boolean);
    } else if (code === ClientPostpartumFlowsheetForms.ClientEstimatedBloodLoss) {
      // Estimated Blood Loss - only Est. Bleeding column
      // Always extract values if hasAnswers is true (to show time), even if estimated is empty
      const eblValues = getEstimatedBloodLossRowValues(observation);
      if (eblValues.hasAnswers) {
        values.estimatedBleeding = eblValues.estimated;
        hasAnswers = true;
      }
    }
  } else if (code === NewbornPostpartumFlowsheetForms.NewbornVitals) {
    // Newborn Vitals - all columns
    const vitals = getNewbornVitalsRowValues(observation);
    values.hr = vitals.heartRate;
    values.rr = vitals.respiratoryRate;
    values.temp = vitals.temperature;
    values.color = vitals.color;
    values.void = vitals.void_;
    values.stool = vitals.stool;
    hasAnswers = Object.values(values).some(Boolean);
  }

  return { values, hasAnswers };
}

/**
 * Determine if an observation should be included in the postpartum flowsheet.
 * Encapsulates all filtering logic for cleaner JSX.
 */
export function isObservationInFlowsheet(
  obs: Observation,
  isClient: boolean,
  validFormCodes: Set<string>,
): boolean {
  const code = getCodeableConceptCode(obs.code);
  if (!code) return false;

  // Exclude observations that shouldn't appear in this flowsheet
  if (shouldExcludeObservation(obs, isClient)) {
    return false;
  }

  // Include if it's a valid form code
  if (validFormCodes.has(code)) {
    return true;
  }

  // Special inclusions: CumulativeBloodLoss and BirthDateTime
  return code === LOINCCodes.CumulativeBloodLoss || code === QuiltedHealthCodes.BirthDateTime;
}
